/**
 * <TransformationShift /> — the emotional turn of the discovery report.
 *
 * Replaces the old side-by-side "Where it breaks down" (challenge grid) +
 * "How Moxo solves it" (solution map), which showed the same challenges twice
 * at the same visual energy — so today and Moxo read as siblings (6 vs 7.5).
 *
 * Here the CONTRAST carries the argument. The left field is deliberately
 * DEADENED — cool grey, dim ink, monochrome severity, no life (a 3/10 world).
 * The right field is ALIVE — warm paper, oxblood accent, a lit edge, and the
 * AI AGENT doing the human's job (a 9/10 world). A grounded scoreboard states
 * the gap in two seconds (manual steps + minutes lost → agent-run + one flow),
 * and each challenge resolves left→right across a transformation gutter.
 *
 * Built to be read at across-the-room screen-share zoom: big type, one story
 * per row, the poles carrying the numbers.
 */
import { useMemo, type CSSProperties } from 'react';

export type Severity = 'high' | 'medium' | 'low';

export interface Challenge {
  id: string;
  title: string;
  description: string;
  severity: Severity;
}

export type SolutionMode = 'ai_runs' | 'human_plus_ai';

export interface MoxoSolution {
  challengeId: string;
  title: string;
  howMoxoHelps: string;
  // Short phrase for the human task a Moxo AI agent takes over here, or null
  // when the solution is a plain workflow feature (no agent doing a person's job).
  agentDoes: string | null;
  // 'ai_runs' = the agent runs this end-to-end; 'human_plus_ai' = the agent does
  // the legwork but a PERSON stays in charge (their role named in humanKeeps).
  // Absent on legacy cached reports → rendered as 'ai_runs'.
  mode?: SolutionMode;
  humanKeeps?: string | null;
}

const SEV_LABEL: Record<Severity, string> = { high: 'High', medium: 'Medium', low: 'Low' };

export default function TransformationShift({
  challenges,
  solutions,
  totalLeakMinutes,
  stepCount,
}: {
  challenges: Challenge[];
  solutions: MoxoSolution[];
  totalLeakMinutes: number;
  stepCount: number;
}) {
  const byId = useMemo(
    () => new Map(solutions.map((s) => [s.challengeId, s])),
    [solutions],
  );
  // Legacy cached reports have no `mode` → count them as agent-run.
  const aiRunsCount = solutions.filter((s) => (s.mode ?? 'ai_runs') === 'ai_runs').length;
  const humanPlusAiCount = solutions.filter((s) => s.mode === 'human_plus_ai').length;
  const highCount = challenges.filter((c) => c.severity === 'high').length;

  return (
    <section className="se-shift" aria-label="Today versus with Moxo">
      <header className="se-shift__head">
        <p className="se-shift__eyebrow">The shift</p>
        <h2 className="se-shift__title">Today, every step loses time. With Moxo, your team and an AI agent share the work.</h2>
        <p className="se-shift__sub">
          On the left is the problem today. On the right, a Moxo AI agent runs the busywork — and your team stays in charge of the decisions.
        </p>
      </header>

      {/* Poles = the dead-simple scoreboard, grounded in real numbers. */}
      <div className="se-shift__poles">
        <div className="se-shift__pole se-shift__pole--today">
          <p className="se-shift__pole-label">Today</p>
          <ul className="se-shift__stats">
            <li><span className="se-shift__stat-num">{stepCount}</span> steps done by hand</li>
            <li><span className="se-shift__stat-num">~{totalLeakMinutes}</span> minutes lost each time</li>
            <li><span className="se-shift__stat-num">{highCount}</span> big problems</li>
          </ul>
        </div>
        <div className="se-shift__pole-gap" aria-hidden="true" />
        <div className="se-shift__pole se-shift__pole--moxo">
          <p className="se-shift__pole-label">With Moxo</p>
          <ul className="se-shift__stats">
            <li><span className="se-shift__stat-num">1</span> place for everything</li>
            <li><span className="se-shift__stat-num">{aiRunsCount}</span> run by an AI agent</li>
            {humanPlusAiCount > 0 ? (
              <li><span className="se-shift__stat-num">{humanPlusAiCount}</span> your team leads, agent helps</li>
            ) : (
              <li><span className="se-shift__stat-num">Full</span> record of every step</li>
            )}
          </ul>
        </div>
      </div>

      <ol className="se-shift__rows">
        {challenges.map((challenge, i) => {
          const solution = byId.get(challenge.id) ?? null;
          return (
            <li className="se-shift__row" key={challenge.id} style={{ '--i': i } as CSSProperties}>
              <div className="se-shift__today">
                <span className={`se-shift__sev se-shift__sev--${challenge.severity}`}>
                  {SEV_LABEL[challenge.severity]}
                </span>
                <h3 className="se-shift__today-title">{challenge.title}</h3>
                <p className="se-shift__today-desc">{challenge.description}</p>
              </div>

              <div className="se-shift__link" aria-hidden="true">
                <span className="se-shift__arrow">&#8594;</span>
              </div>

              <div className="se-shift__moxo">
                <div className="se-shift__moxo-tags">
                  {solution?.mode === 'human_plus_ai' ? (
                    <span className="se-shift__agent se-shift__agent--human">Human + AI</span>
                  ) : solution?.agentDoes ? (
                    <span className="se-shift__agent">AI Agent</span>
                  ) : (
                    <span className="se-shift__moxo-tag">Moxo</span>
                  )}
                </div>
                <h3 className="se-shift__moxo-title">{solution?.title ?? '—'}</h3>
                {solution?.agentDoes && (
                  <p className="se-shift__handles">
                    <span className="se-shift__handles-label">Agent does</span>
                    {solution.agentDoes}
                  </p>
                )}
                {solution?.mode === 'human_plus_ai' && solution.humanKeeps && (
                  <p className="se-shift__handles se-shift__handles--human">
                    <span className="se-shift__handles-label">You keep</span>
                    {solution.humanKeeps}
                  </p>
                )}
                {solution && <p className="se-shift__moxo-desc">{solution.howMoxoHelps}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
